if (!customElements.get("artisan-carousel")) {
  class ArtisanCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideContainer: ".artisan-carousel-track",
        slideWrapper: ".artisan-carousel-slides",
        slides: ".artisan-carousel-slide",
        prevButton: ".artisan-nav-prev",
        nextButton: ".artisan-nav-next"
      };

      this.classes = {
        active: 'active'
      };

      this.slider = null;
      this.currentSlide = 0;

      // Get data attributes
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5;
      this.showNavigation = this.dataset.showNavigation === "true";
      this.total = parseInt(this.dataset.total) || 0;

      this.init();
    }

    init() {
      this.initSlider();
    }

    initSlider() {
      const slideContainer = this.querySelector(this.selectors.slideContainer);
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);

      if (!slideContainer) return;

      // Swiper configuration
      this.options = {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: this.total > 1,
        autoplay: this.autoplay ? {
          delay: this.autoplaySpeed * 1000,
          disableOnInteraction: false,
        } : false,
        navigation: (prevButton && nextButton) ? {
          nextEl: nextButton,
          prevEl: prevButton,
        } : false,
        breakpoints: {
          768: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
          1024: {
            slidesPerView: 1,
            spaceBetween: 0,
          }
        },
        on: {
          init: () => {
            this.updateActiveStates();
          },
          slideChange: () => {
            this.updateActiveStates();
          }
        }
      };

      // Initialize Swiper if available
      if (typeof Swiper !== 'undefined') {
        this.slider = new Swiper(slideContainer, this.options);
      } else if (typeof MinimogLibs !== 'undefined' && MinimogLibs.Swiper) {
        this.slider = new MinimogLibs.Swiper(slideContainer, this.options);
      } else {
        // Fallback: Simple manual carousel
        this.initFallbackCarousel();
      }
    }

    initFallbackCarousel() {
      const slideWrapper = this.querySelector(this.selectors.slideWrapper);
      const slides = this.querySelectorAll(this.selectors.slides);
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);

      if (!slideWrapper || slides.length === 0) return;

      // Set up manual navigation
      if (prevButton) {
        prevButton.addEventListener('click', () => this.prevSlide());
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => this.nextSlide());
      }

      // Set up autoplay
      if (this.autoplay) {
        this.startAutoplay();
      }

      // Initialize first slide
      this.updateSlidePosition();
      this.updateActiveStates();

      // Pause autoplay on hover
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });
    }

    nextSlide() {
      const slides = this.querySelectorAll(this.selectors.slides);
      this.currentSlide = (this.currentSlide + 1) % slides.length;
      this.updateSlidePosition();
      this.updateActiveStates();
    }

    prevSlide() {
      const slides = this.querySelectorAll(this.selectors.slides);
      this.currentSlide = this.currentSlide === 0 ? slides.length - 1 : this.currentSlide - 1;
      this.updateSlidePosition();
      this.updateActiveStates();
    }

    updateSlidePosition() {
      const slideWrapper = this.querySelector(this.selectors.slideWrapper);
      if (slideWrapper) {
        const translateX = -this.currentSlide * 100;
        slideWrapper.style.transform = `translateX(${translateX}%)`;
      }
    }

    updateActiveStates() {
      // Update slides
      const slides = this.querySelectorAll(this.selectors.slides);
      slides.forEach((slide, index) => {
        slide.classList.toggle(this.classes.active, index === this.currentSlide);
      });

      // Update navigation buttons
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);
      
      if (slides.length <= 1) {
        if (prevButton) prevButton.style.display = 'none';
        if (nextButton) nextButton.style.display = 'none';
      } else {
        if (prevButton) prevButton.style.display = 'flex';
        if (nextButton) nextButton.style.display = 'flex';
      }
    }

    startAutoplay() {
      this.stopAutoplay(); // Clear any existing autoplay
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed * 1000);
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    // Touch/swipe support for mobile
    addTouchSupport() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = false;
      });

      this.addEventListener('touchmove', (e) => {
        if (!isDragging) {
          const deltaX = Math.abs(e.touches[0].clientX - startX);
          const deltaY = Math.abs(e.touches[0].clientY - startY);
          isDragging = deltaX > deltaY && deltaX > 10;
        }
      });

      this.addEventListener('touchend', (e) => {
        if (isDragging) {
          const endX = e.changedTouches[0].clientX;
          const deltaX = endX - startX;
          
          if (Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
              this.prevSlide();
            } else {
              this.nextSlide();
            }
          }
        }
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.slider && this.slider.destroy) {
        this.slider.destroy();
      }
    }
  }

  customElements.define("artisan-carousel", ArtisanCarousel);
}

